// ── State ────────────────────────────────────────────
const S = {
  shape:null, colour:null, sides:null,
  metallicId:null, premium:null,
  kg:null, days:null, file:null,
};

// ── Helpers ──────────────────────────────────────────
const eur = n => '€\u202f' + n.toLocaleString('nl-NL',{minimumFractionDigits:2,maximumFractionDigits:2});

function tierPrice(tiers, kg) {
  return [...tiers].filter(t=>kg>=t.kg).sort((a,b)=>b.kg-a.kg)[0]?.p ?? null;
}

// premium maps to 'full' for product/supplier lookup
function productColour() { return S.colour==='premium' ? 'full' : S.colour; }
function isMetallic()    { return S.colour==='metallic'; }
function isPremium()     { return S.colour==='premium'; }

function availColours() {
  if (!S.shape) return ['single','full','metallic'];
  return [...new Set(PRODUCTS.filter(p=>p.shape===S.shape).map(p=>p.colour))];
}

function availSides() {
  const pc = productColour();
  // If no shape or no colour chosen yet, nothing is available
  if (!S.shape || !pc || isMetallic()) return [];
  return [...new Set(
    PRODUCTS
      .filter(p => p.shape===S.shape && p.colour===pc && p.sides!=='none')
      .map(p => p.sides)
  )];
}

// Get delivery options for a product — respects product.days if set (Wuxi split pricing)
function productDeliveryOpts(p, kg) {
  const supOpts = SUP[p.sup](kg, p.shape) || [];
  if (p.days != null) {
    // Product is pinned to a specific lead time (Wuxi air/sea split)
    const match = supOpts.find(o => o.days === p.days);
    return match ? [match] : [];
  }
  return supOpts;
}

function deliveryOpts(kg) {
  const pc    = productColour();
  const sides = isMetallic() ? 'none' : S.sides;
  if (!S.shape||!pc||(!sides&&!isMetallic())) return [];
  const map = new Map();
  PRODUCTS
    .filter(p=>p.shape===S.shape&&p.colour===pc&&p.sides===sides)
    .forEach(p => productDeliveryOpts(p, kg).forEach(o => {
      if(!map.has(o.days)) map.set(o.days, o.label);
    }));
  return [...map.entries()].sort((a,b)=>a[0]-b[0]).map(([days,label])=>({days,label}));
}

function findBest() {
  const pc    = productColour();
  const sides = isMetallic() ? 'none' : S.sides;
  if (!S.shape||!pc||!S.kg||!S.days) return null;
  if (!isMetallic() && !sides) return null;
  let best = null;
  PRODUCTS
    .filter(p=>p.shape===S.shape&&p.colour===pc&&p.sides===sides)
    .forEach(p => productDeliveryOpts(p, S.kg).forEach(o => {
      if(o.days === S.days && o.days !== 999){
        const perKg = tierPrice(p.tiers, S.kg);
        if(perKg==null) return;
        const total = perKg * S.kg;
        if(!best||total<best.total)
          best={total, perKg, days:o.days, label:o.label, sup:p.sup};
      }
    }));
  return best;
}

// ── Tile builder ─────────────────────────────────────
function tile(val,label,sub,active,disabled,cb){
  const b=document.createElement('button');
  b.type='button';
  b.className='mfx-tile'+(active?' mfx-on':'')+(disabled?' mfx-off':'');
  b.innerHTML=`<span class="mfx-tile-name">${label}</span>${sub?`<span class="mfx-tile-sub">${sub}</span>`:''}`;
  if(!disabled)b.addEventListener('click',()=>cb(val));
  return b;
}

// ── Render shapes ─────────────────────────────────────
function renderShapes(){
  const el=document.getElementById('shapeGrid');el.innerHTML='';
  SHAPES.forEach(s=>el.appendChild(tile(s.id,s.label,s.sub,S.shape===s.id,false,v=>{
    S.shape=v; S.days=null;
    // reset colour if not available for new shape
    if(S.colour&&!availColours().includes(S.colour)){S.colour=null;S.sides=null;}
    if(S.sides&&!availSides().includes(S.sides))S.sides=null;
    renderAll();
  })));
}

// ── Render colour type + sub-sections ────────────────
function renderColour(){
  const el=document.getElementById('colourGrid');el.innerHTML='';
  // Premium uses same supplier/price logic as 'full' — map it to full for product lookup
  const av=availColours(); // returns 'single','full','metallic' based on PRODUCTS
  CTYPES.forEach(c=>{
    // premium is available wherever full is available
    const isAvail = c.id==='premium' ? av.includes('full') : av.includes(c.id);
    el.appendChild(tile(c.id,c.label,c.sub,S.colour===c.id,!isAvail,v=>{
      S.colour=v; S.sides=null; S.metallicId=null; S.days=null;
      renderAll();
    }));
  });

  // sub-sections
  document.getElementById('singleSection').classList.toggle('mfx-hidden', S.colour!=='single');
  document.getElementById('fullSection').classList.toggle('mfx-hidden', S.colour!=='full');
  document.getElementById('premiumSection').classList.toggle('mfx-hidden', S.colour!=='premium');
  document.getElementById('metallicSection').classList.toggle('mfx-hidden', S.colour!=='metallic');

  // metallic swatches
  if(S.colour==='metallic') buildMetallicSwatches();
}

// ── Metallic swatches ─────────────────────────────────
function buildMetallicSwatches(){
  const el=document.getElementById('metallicSwatches');el.innerHTML='';
  METALLICS.forEach(c=>{
    const b=document.createElement('button');b.type='button';
    b.className='mfx-sw'+(S.metallicId===c.id?' mfx-on':'');
    b.style.background=c.hex;
    b.innerHTML=`<span class="mfx-sw-tip">${c.name}</span>`;
    b.addEventListener('click',()=>{
      S.metallicId=c.id;
      buildMetallicSwatches();
      // update pill
      document.getElementById('metallicDot').style.background=c.hex;
      document.getElementById('metallicName').textContent=c.name;
      document.getElementById('metallicPill').classList.add('mfx-show');
      update();
    });
    el.appendChild(b);
  });
}

// ── Render sides step ─────────────────────────────────
function renderSides(){
  const sidesStep   = document.getElementById('sidesStep');
  const dividerSides = document.getElementById('dividerSides');

  // Show sides step only when shape AND colour are chosen, AND colour is not metallic
  const showSides = !!(S.shape && S.colour && !isMetallic());

  sidesStep.style.display    = showSides ? '' : 'none';
  dividerSides.style.display = showSides ? '' : 'none';

  if (!showSides) return;

  const el = document.getElementById('sidesGrid');
  el.innerHTML = '';
  const av = availSides();
  SIDES.forEach(o => el.appendChild(
    tile(o.id, o.label, o.sub, S.sides===o.id, !av.includes(o.id), v => {
      S.sides = v; S.days = null;
      renderSides(); renderArtwork(); renderDelivery(); update();
    })
  ));
}

// ── Render artwork step ───────────────────────────────
function renderArtwork(){
  const artStep        = document.getElementById('artworkStep');
  const dividerArtwork = document.getElementById('dividerArtwork');
  const dieGuidelines  = document.getElementById('dieGuidelines');
  const tstrip         = document.getElementById('templateStrip');
  const stepSub        = document.getElementById('artworkStepSub');

  const sidesOk = isMetallic() || !!S.sides;

  // Show artwork step when:
  // - non-metallic: shape + colour + sides all chosen
  // - custom metallic: shape + colour chosen (no sides step)
  const showArtwork = !!(S.shape && S.colour && sidesOk) &&
    (!isMetallic() || S.shape === 'custom');

  artStep.style.display        = showArtwork ? '' : 'none';
  dividerArtwork.style.display = showArtwork ? '' : 'none';

  if (!showArtwork) return;

  const isCustom         = S.shape === 'custom';
  const isRectangle      = S.shape === 'rectangle';
  const isCustomMetallic = isCustom && isMetallic();

  // Die-cut guidelines: only for custom shapes
  dieGuidelines.style.display = isCustom ? '' : 'none';

  // Template strip: standard shapes + rectangle (not custom, not metallic)
  tstrip.style.display = (!isCustom && !isMetallic()) ? '' : 'none';
  if (!isCustom && !isMetallic()) {
    const tmpl = TEMPLATES[S.shape];
    const link = document.getElementById('templateLink');
    link.href = tmpl?.file || '#';
    link.setAttribute('aria-label', `Download ${tmpl?.label||'template'}`);
  }

  // Step description
  if (isCustomMetallic) {
    stepSub.textContent = 'Upload your die-cut shape outline as a vector PDF. No artwork needed — the metallic colour is applied to the shape as a whole. Read the shape requirements above first.';
  } else if (isCustom) {
    stepSub.textContent = 'Upload your die-cut outline as a vector PDF. Read the shape requirements above first.';
  } else if (isRectangle) {
    stepSub.textContent = 'Download the template, specify your dimensions, place your artwork and upload below. Any rectangular or square size is possible — no die cost applies.';
  } else {
    stepSub.textContent = 'Download the template for your shape, place your artwork within the bleed area, and upload the file below.';
  }

  // Review box
  document.getElementById('reviewTitle').textContent = isCustom
    ? 'Your shape will be reviewed by our team.'
    : 'Your artwork will be checked by our team.';
  document.getElementById('reviewBody').textContent = isCustom
    ? 'After submitting, a MAGICFX specialist checks your die-cut and confirms feasibility within 1 business day.'
    : 'After submitting, our team verifies your artwork fits the template and confirms production within 1 business day.';
}

// ── Render delivery ───────────────────────────────────
function renderDelivery(){
  const el=document.getElementById('deliveryChips');
  const hint=document.getElementById('deliveryHint');
  el.innerHTML='';
  const ready = S.shape && S.colour && (isMetallic() || S.sides);
  if(!ready){hint.textContent='Complete steps 01–03 first.';return;}
  hint.textContent='';
  const opts=deliveryOpts(S.kg||10);
  if(!opts.length){hint.textContent='No delivery options for this configuration.';return;}
  opts.forEach(o=>{
    const c=document.createElement('button');c.type='button';
    const isTbd = o.days === 999;
    c.className='mfx-chip'+(S.days===o.days?' mfx-on':'')+(isTbd?' mfx-khaki':'');
    c.textContent=o.label;
    if(isTbd){
      c.style.cursor='default';
      c.addEventListener('click',()=>{
        document.getElementById('deliveryHint').textContent='Lead time for > 250 kg is not yet available. Please contact us directly.';
      });
    } else {
      c.addEventListener('click',()=>{S.days=o.days;renderDelivery();update();});
    }
    el.appendChild(c);
  });
}

function renderAll(){renderShapes();renderColour();renderSides();renderArtwork();renderDelivery();update();}

// ── Completeness checks ───────────────────────────────
function colourComplete(){
  if(!S.colour)return false;
  if(S.colour==='metallic')return!!S.metallicId;
  return true; // single / full / premium: always complete once chosen
}

function artworkComplete(){
  // Standard metallic (non-custom) = no artwork needed
  if(isMetallic() && S.shape !== 'custom') return true;
  // Artwork only required when the step is visible
  if(!S.sides && !isMetallic()) return true; // not yet at that step
  return !!S.file;
}

function sidesComplete(){
  if(S.colour==='metallic')return true;
  return!!S.sides;
}

// ── Update result ─────────────────────────────────────
function update(){
  const priceEl=document.getElementById('resPrice');
  const quoteEl=document.getElementById('resQuote');
  const metaEl=document.getElementById('resMeta');
  const ctaEl=document.getElementById('cta');
  const extEl=document.getElementById('extras');

  const kg=S.kg;
  document.getElementById('kgWarn').textContent=kg&&kg>0&&kg<10?'Minimum order is 10 kg.':'';
  const kgOk=kg!=null&&kg>=10;

  const clear=msg=>{
    priceEl.textContent='€ —';priceEl.classList.add('mfx-empty');priceEl.style.display='';
    quoteEl.style.display='none';
    document.getElementById('priceBreakdown').style.display='none';
    metaEl.textContent=msg;ctaEl.disabled=true;ctaEl.classList.remove('mfx-quote');extEl.style.display='none';
  };

  if(!S.shape)              return clear('Choose a shape at step 01.');
  if(!colourComplete())     return clear('Choose a colour type at step 02.');
  if(!sidesComplete())      return clear('Choose print sides at step 03.');
  if(!artworkComplete())    return clear('Upload your artwork at step 03b.');
  if(!kgOk)                 return clear('Enter a quantity (min. 10 kg) at step 04.');
  if(!S.days)               return clear('Choose a delivery option at step 05.');

  // Premium colour type → always "on request"
  if(isPremium()){
    priceEl.style.display='none';quoteEl.style.display='';
    const shL=SHAPES.find(x=>x.id===S.shape)?.label||S.shape;
    const siL=SIDES.find(x=>x.id===S.sides)?.label||'';
    metaEl.innerHTML=`<strong>${shL}</strong> · ${siL} · Pantone exact match<br>${kg.toLocaleString('nl-NL')} kg`;
    ctaEl.disabled=false;ctaEl.classList.add('mfx-quote');ctaEl.textContent='Continue to quote';
    extEl.style.display='none';
    return;
  }

  priceEl.style.display='';quoteEl.style.display='none';
  const r=findBest();
  if(!r)return clear('No product found for this configuration.');

  priceEl.classList.remove('mfx-empty');
  priceEl.textContent=eur(r.total);
  ctaEl.disabled=false;ctaEl.classList.remove('mfx-quote');ctaEl.textContent='Continue to quote';

  // Price breakdown — die cost only for custom shape (not rectangle, not standard shapes)
  const needsDie = S.shape==='custom';
  const breakdown = document.getElementById('priceBreakdown');
  const plConfetti = document.getElementById('plConfetti');
  const plDieRow   = document.getElementById('plDieRow');
  const plTotalRow = document.getElementById('plTotalRow');
  const plTotal    = document.getElementById('plTotal');

  breakdown.style.display = '';
  plConfetti.textContent   = eur(r.total);

  if(needsDie){
    plDieRow.style.display   = '';
    plTotalRow.style.display = '';
    plTotal.textContent      = eur(r.total + TOOLING_COST);
  } else {
    plDieRow.style.display   = 'none';
    plTotalRow.style.display = 'none';
  }

  const shL=SHAPES.find(x=>x.id===S.shape)?.label||S.shape;
  let colDesc='';
  if(S.colour==='single')    colDesc='Single colour (CMYK)';
  else if(S.colour==='full') colDesc='Full colour (CMYK)';
  else if(isMetallic()){
    const m=METALLICS.find(x=>x.id===S.metallicId);
    colDesc=`Metallic — ${m?.name||''}`;
  }
  const siL=isMetallic()?'No print':(SIDES.find(x=>x.id===S.sides)?.label||'');

  metaEl.innerHTML=
    `<strong>${shL}</strong> · ${siL} · ${colDesc}<br>`+
    `${kg.toLocaleString('nl-NL')} kg at ${eur(r.perKg)}/kg · ${r.label}`;

  const lines=[];
  // For custom shapes the die cost is already shown in the price breakdown above
  // Only add a note here if there are other extras
  extEl.style.display=lines.length?'':'none';
  if(lines.length)extEl.innerHTML=lines.join('<br>');
}

// ── Listeners ─────────────────────────────────────────
document.getElementById('kgIn').addEventListener('input',e=>{
  const v=parseFloat(e.target.value);
  S.kg=isNaN(v)?null:v;
  document.querySelectorAll('#qtyChips .mfx-chip').forEach(c=>c.classList.remove('mfx-on'));
  S.days=null;renderDelivery();update();
});
document.querySelectorAll('#qtyChips .mfx-chip').forEach(chip=>{
  chip.addEventListener('click',()=>{
    const v=parseInt(chip.dataset.kg,10);
    document.getElementById('kgIn').value=v;S.kg=v;
    document.querySelectorAll('#qtyChips .mfx-chip').forEach(c=>c.classList.remove('mfx-on'));
    chip.classList.add('mfx-on');S.days=null;renderDelivery();update();
  });
});

// [data-pm] buttons are now display-only indicators inside the premium section — no listener needed

// file upload
function resetFile(){
  document.getElementById('fileRow').classList.remove('mfx-show');
  document.getElementById('uploadZone').style.display='';
  document.getElementById('fileInput').value='';
  S.file=null;
}
(function(){
  const zone=document.getElementById('uploadZone');
  const input=document.getElementById('fileInput');
  const row=document.getElementById('fileRow');
  const lbl=document.getElementById('fileLabel');
  const rm=document.getElementById('fileRm');
  function setFile(f){
    if(!f)return;
    if(f.size>20*1024*1024){alert('File is too large — max 20 MB.');return;}
    S.file=f;lbl.textContent=f.name;
    row.classList.add('mfx-show');zone.style.display='none';update();
  }
  input.addEventListener('change',()=>{if(input.files[0])setFile(input.files[0]);});
  rm.addEventListener('click',()=>{resetFile();update();});
  zone.addEventListener('dragover',e=>{e.preventDefault();zone.classList.add('mfx-over');});
  zone.addEventListener('dragleave',()=>zone.classList.remove('mfx-over'));
  zone.addEventListener('drop',e=>{e.preventDefault();zone.classList.remove('mfx-over');if(e.dataTransfer.files[0])setFile(e.dataTransfer.files[0]);});
})();

// ── Step 06 — show/hide ──────────────────────────────
function isConfigComplete(){
  const kgOk = S.kg != null && S.kg >= 10;
  return !!(S.shape && colourComplete() && sidesComplete() && artworkComplete() && kgOk && S.days);
}

// Hook into update() to also toggle step 06
const _baseUpdate = update;
update = function(){
  _baseUpdate();
  document.getElementById('step06wrap').style.display = isConfigComplete() ? '' : 'none';
};

// CTA → scroll to step 06
document.getElementById('cta').addEventListener('click', () => {
  if(!isConfigComplete()) return;
  const el = document.getElementById('step06wrap');
  el.style.display = '';
  setTimeout(() => el.scrollIntoView({behavior:'smooth', block:'start'}), 50);
});

// ── Helpers ──────────────────────────────────────────
function makeRef(){
  const d = new Date(), pad = n => String(n).padStart(2,'0');
  return `MFX-${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}-${Math.random().toString(36).slice(2,6).toUpperCase()}`;
}

function configLines(){
  const shL = SHAPES.find(x=>x.id===S.shape)?.label || S.shape;
  const siL = isMetallic() ? 'No print' : (SIDES.find(x=>x.id===S.sides)?.label || '');
  const coL = S.colour==='single'   ? 'Single colour (CMYK)'
            : S.colour==='full'     ? 'Full colour (CMYK)'
            : S.colour==='premium'  ? 'Premium — Pantone exact match'
            : (() => { const m=METALLICS.find(x=>x.id===S.metallicId); return `Metallic — ${m?.name||''}`; })();
  const r   = findBest();
  const priceStr = isPremium() ? 'On request (Pantone surcharge TBD)'
                               : r ? `${eur(r.total)} (${eur(r.perKg)}/kg)` : '—';
  const lines = [
    ['Shape',             shL],
    ['Print sides',       siL],
    ['Colour / print',    coL],
    ['Quantity',          `${S.kg?.toLocaleString('nl-NL')} kg`],
    ['Delivery',          r ? r.label : '—'],
    ['Price (excl. VAT)', priceStr],
  ];
  if(S.shape==='custom' && r)
    lines.push(['Die-cut blade (one-time)', `${eur(TOOLING_COST)} — owned by customer, stored at factory`]);
  if(S.file) lines.push(['Artwork file', S.file.name]);
  return lines;
}

function supInfo(){
  const r = findBest();
  if(!r) return {};
  const names = {thijsen:'Thijsen Drukkerij Geldermalsen',snep:'Drukkerij Snep BV',wuxi:'Wuxi Glee Salute Co., Ltd.'};
  return {
    name:     names[r.sup] || r.sup,
    delivery: r.label,
    transport: r.sup==='wuxi' ? '€10/kg (air) or €0.10/kg (sea)' : '€2/kg (NL paper)',
    tooling:  S.shape==='custom' ? `${eur(TOOLING_COST)} one-time (owned by customer, stored at factory)` : 'None (standard die available)',
    sup:      r.sup,
  };
}

// ── PDF download ─────────────────────────────────────
function generatePDF(ref, cust){
  const r       = findBest();
  const now     = new Date();
  const expiry  = new Date(now); expiry.setDate(expiry.getDate() + 30);
  const fmtDate = d => d.toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'});
  const quoteNum = ref.replace('MFX-','').replace('-','');

  const shL = SHAPES.find(x=>x.id===S.shape)?.label || S.shape;
  const siL = isMetallic() ? 'No print' : (SIDES.find(x=>x.id===S.sides)?.label || '');
  const coL = S.colour==='single'  ? 'Single colour (CMYK)'
            : S.colour==='full'    ? 'Full colour (CMYK)'
            : S.colour==='premium' ? 'Premium — Pantone exact match'
            : (()=>{ const m=METALLICS.find(x=>x.id===S.metallicId); return `Metallic — ${m?.name||''}`; })();

  const confettiPrice = r ? r.perKg : 0;
  const confettiTotal = r ? r.total : 0;
  const isCustom      = S.shape === 'custom';
  const grandTotal    = confettiTotal + (isCustom ? TOOLING_COST : 0);

  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8">
<title>MAGICFX Quote ${quoteNum}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'Inter',Arial,sans-serif;font-size:9.5pt;color:#111;background:#fff;}
.page{max-width:820px;margin:0 auto;padding:44px 52px 52px;}

.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px;}
.quote-box{background:#111;color:#fff;padding:12px 20px;border-radius:4px;line-height:1;}
.quote-box span{display:block;font-size:8pt;font-weight:500;letter-spacing:.1em;text-transform:uppercase;opacity:.6;margin-bottom:4px;}
.quote-box h1{font-size:17pt;font-weight:700;letter-spacing:-.01em;}
.logo{font-weight:800;font-size:22pt;letter-spacing:.12em;color:#111;}

.dates{display:flex;gap:40px;justify-content:flex-end;padding-bottom:20px;border-bottom:2px solid #111;margin-bottom:28px;}
.date-col{text-align:center;}
.date-col .dl{font-size:7pt;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#999;margin-bottom:4px;}
.date-col .dv{font-size:9pt;font-weight:600;}

.parties{display:grid;grid-template-columns:1fr 1fr;gap:28px;margin-bottom:28px;}
.party .ph{font-size:7pt;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#999;margin-bottom:6px;}
.party .pn{font-weight:700;font-size:10pt;margin-bottom:4px;}
.party .pl{font-size:8.5pt;color:#444;line-height:1.75;}

.addr-bar{display:grid;grid-template-columns:1fr 1fr 1fr;border:1px solid #ddd;margin-bottom:28px;}
.ac{padding:16px 18px;border-right:1px solid #ddd;}
.ac:last-child{border-right:none;}
.ac-h{font-size:7pt;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#999;margin-bottom:8px;}
.ac-b{font-size:8.5pt;color:#222;line-height:1.75;}

table{width:100%;border-collapse:collapse;}
thead tr{background:#111;color:#fff;}
thead th{padding:10px 14px;font-size:7.5pt;font-weight:600;letter-spacing:.07em;text-transform:uppercase;text-align:left;}
thead th.r{text-align:right;}
tbody td{padding:13px 14px;font-size:9pt;vertical-align:top;border-bottom:1px solid #eee;}
tbody td.r{text-align:right;white-space:nowrap;}
tbody tr:last-child td{border-bottom:2px solid #111;}
.pn{color:#bbb;font-size:8pt;font-weight:400;display:block;margin-bottom:1px;}
.pt{font-weight:600;font-size:9.5pt;}
.pd{font-size:8pt;color:#888;margin-top:3px;}

.totals-wrap{display:flex;justify-content:flex-end;margin-top:0;}
.totals{width:280px;}
.tr{display:flex;justify-content:space-between;padding:7px 14px;font-size:9pt;border-bottom:1px solid #eee;}
.tr:last-child{border-bottom:none;}
.tr.final{background:#111;color:#fff;padding:14px 16px;margin-top:2px;}
.tr .tl{color:#666;}
.tr.final .tl{color:rgba(255,255,255,.7);font-size:9pt;}
.tr.final .tv{font-size:13pt;font-weight:700;}

.notices{margin-top:28px;padding-top:20px;border-top:1px solid #eee;}
.nt{font-size:7.5pt;color:#aaa;line-height:1.8;margin-bottom:3px;}
.die-box{margin-top:16px;background:#f8f5ee;border:1px solid #e0d4b8;border-radius:4px;padding:12px 16px;font-size:8.5pt;color:#555;line-height:1.6;}
.premium-box{margin-top:16px;background:#fffbf0;border:1px solid #f0d880;border-radius:4px;padding:12px 16px;font-size:8.5pt;color:#666;}

.footer{margin-top:32px;padding-top:14px;border-top:1px solid #e8e8e8;display:flex;justify-content:space-between;align-items:flex-end;}
.fl{font-size:7.5pt;color:#bbb;line-height:1.8;}
.fr{font-size:7.5pt;color:#bbb;text-align:right;}

/* Print button — hidden when printing */
.print-btn{
  position:fixed;bottom:28px;right:28px;
  background:#111;color:#fff;border:none;border-radius:40px;
  padding:13px 24px;font-family:inherit;font-size:13px;font-weight:500;
  cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,.25);
  display:flex;align-items:center;gap:8px;
  transition:opacity .15s;z-index:99;
}
.print-btn:hover{opacity:.85;}
@media print{
  .print-btn{display:none!important;}
  body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .page{padding:24px 32px;}
}
</style></head><body>

<button class="print-btn" onclick="window.print()">
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
  Save as PDF
</button>

<div class="page">

  <div class="header">
    <div class="quote-box">
      <span>Quote</span>
      <h1>${quoteNum}</h1>
    </div>
    <div class="logo">MAGICFX</div>
  </div>

  <div class="dates">
    <div class="date-col"><div class="dl">Quoted</div><div class="dv">${fmtDate(now)}</div></div>
    <div class="date-col"><div class="dl">Expiration</div><div class="dv">${fmtDate(expiry)}</div></div>
  </div>

  <div class="parties">
    <div class="party">
      <div class="ph">Customer</div>
      <div class="pn">${cust.name}</div>
      <div class="pl">
        ${cust.company}
        ${cust.vat ? `<br>VAT: ${cust.vat}` : ''}
        <br>${cust.email}
      </div>
    </div>
    <div class="party" style="text-align:right">
      <div class="ph">From</div>
      <div class="pn">MAGICFX B.V.</div>
      <div class="pl">
        Schouwrooij 27<br>
        5281 RE Boxtel<br>
        Netherlands<br>
        support@magicfx.com
      </div>
    </div>
  </div>

  <div class="addr-bar">
    <div class="ac">
      <div class="ac-h">Billing</div>
      <div class="ac-b">${cust.name}<br>${cust.company}<br>${cust.address}</div>
    </div>
    <div class="ac">
      <div class="ac-h">Shipping</div>
      <div class="ac-b">${cust.name}<br>${cust.company}<br>${cust.address}<br>${cust.email}</div>
    </div>
    <div class="ac">
      <div class="ac-h">Delivery</div>
      <div class="ac-b">
        ${r ? r.label : '—'}
        ${cust.notes ? `<br><br><em style="color:#888">${cust.notes}</em>` : ''}
      </div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:32px"></th>
        <th>Product</th>
        <th class="r">Price / kg</th>
        <th class="r">Qty</th>
        <th class="r">Unit</th>
        <th class="r">Subtotal</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><span style="color:#bbb;font-size:8pt">1</span></td>
        <td>
          <span class="pn">Custom printed confetti</span>
          <span class="pt">${shL}${siL ? ' — ' + siL : ''}</span>
          <div class="pd">${coL}${S.file ? ` &nbsp;·&nbsp; 📄 ${S.file.name}` : ''}</div>
        </td>
        <td class="r">${isPremium() ? '<em style="color:#aaa">On request</em>' : eur(confettiPrice)}</td>
        <td class="r">${S.kg?.toLocaleString('nl-NL')}</td>
        <td class="r">kg</td>
        <td class="r">${isPremium() ? '<em style="color:#aaa">On request</em>' : eur(confettiTotal)}</td>
      </tr>
      ${isCustom ? `<tr>
        <td><span style="color:#bbb;font-size:8pt">2</span></td>
        <td>
          <span class="pn">One-time tooling</span>
          <span class="pt">Die-cut blade</span>
          <div class="pd">Your property · stored at our factory · not charged on reorders</div>
        </td>
        <td class="r">${eur(TOOLING_COST)}</td>
        <td class="r">1</td>
        <td class="r">piece</td>
        <td class="r">${eur(TOOLING_COST)}</td>
      </tr>` : ''}
    </tbody>
  </table>

  <div class="totals-wrap">
    <div class="totals">
      ${!isPremium() ? `<div class="tr"><span class="tl">Subtotal</span><span>${eur(grandTotal)}</span></div>
      <div class="tr"><span class="tl">VAT</span><span style="color:#aaa">Not included</span></div>` : ''}
      <div class="tr final">
        <span class="tl">Total</span>
        <span class="tv">${isPremium() ? 'On request' : eur(grandTotal)}</span>
      </div>
    </div>
  </div>

  <div class="notices">
    <p class="nt">Thank you for your interest. Questions? Contact us at support@magicfx.com.</p>
    <p class="nt">Pricing is valid for 30 days from the quote date.</p>
    <p class="nt">This quotation is provided solely for the review and approval of pricing. It does not constitute a guarantee of product availability or a reservation of any products.</p>
    ${isPremium() ? `<div class="premium-box"><strong>Premium colour (Pantone):</strong> Final price is on request. A MAGICFX specialist will confirm the exact surcharge within 1 business day.</div>` : ''}
    ${isCustom ? `<div class="die-box"><strong>Die-cut blade · ${eur(TOOLING_COST)}</strong> — The die belongs to you and is stored at our factory. You are only charged once per unique shape. Reorders of the same shape do not include this cost.</div>` : ''}
  </div>

  <div class="footer">
    <div class="fl">MAGICFX B.V. · Schouwrooij 27 · 5281 RE Boxtel · Netherlands</div>
    <div class="fr">Ref: ${ref} · ${fmtDate(now)}</div>
  </div>

</div>
</body></html>`;

  // Open in new tab so user can print → Save as PDF
  const blob = new Blob([html], {type:'text/html'});
  const url  = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

// ── Mailto builders ──────────────────────────────────
function buildCustomerMail(ref, cust){
  const r = findBest();
  const summary = configLines().map(([k,v])=>`${k}: ${v}`).join('\n');
  const priceBlock = isPremium()
    ? `Price: On request\nA MAGICFX specialist will confirm the Pantone surcharge and final price within 1 business day.`
    : r ? `Total price (excl. VAT): ${eur(r.total)}\nPrice per kg: ${eur(r.perKg)}\nDelivery: ${r.label}` : '';

  const body = `Dear ${cust.name},\n\nThank you for your quote request. We have received your configuration and will get back to you within 1 business day.\n\n───────────────────\nYOUR ORDER SUMMARY\n───────────────────\n${summary}\n\n${priceBlock}\n\nReference number: ${ref}\n\nIf you have any questions, please reply to this email.\n\nKind regards,\nMAGICFX Customer Support\nsupport@magicfx.com`;

  return `mailto:${encodeURIComponent(cust.email)}?subject=${encodeURIComponent(`MAGICFX Quote Request — ${ref}`)}&body=${encodeURIComponent(body)}`;
}

function buildSupportMail(ref, cust){
  const r   = findBest();
  const sup = supInfo();
  const summary = configLines().map(([k,v])=>`${k}: ${v}`).join('\n');

  const body = [
    `NEW QUOTE REQUEST — ${ref}`,
    `Received: ${new Date().toLocaleString('en-GB')}`,
    ``,
    `─── CUSTOMER ───────────────────────`,
    `Name:     ${cust.name}`,
    `Company:  ${cust.company}`,
    `Email:    ${cust.email}`,
    `VAT:      ${cust.vat||'—'}`,
    `Address:  ${cust.address}`,
    cust.notes ? `Notes:    ${cust.notes}` : null,
    ``,
    `─── CONFIGURATION ──────────────────`,
    summary,
    ``,
    `─── SUPPLIER / INKOOP ──────────────`,
    `Supplier:  ${sup.name}`,
    `Lead time: ${sup.delivery}`,
    `Transport: ${sup.transport}`,
    `Tooling:   ${sup.tooling}`,
    ``,
    isPremium() ? `⚠ PREMIUM COLOUR — Pantone surcharge TBD. Request supplier quote before confirming price to customer.` : null,
    S.file      ? `⚠ ARTWORK: ${S.file.name} — attached below.` : null,
    ``,
    `Full quote PDF is attached (MAGICFX_Quote_${ref}.html).`,
    ``,
    `— MAGICFX Calculator`,
  ].filter(l => l !== null).join('\n');

  return `mailto:${encodeURIComponent('support@magicfx.com')}?subject=${encodeURIComponent(`[ACTION] Quote ${ref} — ${cust.company}`)}&body=${encodeURIComponent(body)}`;
}

// ── Email preview modal ──────────────────────────────
function getPreviewCustomer(ref, cust){
  const r = findBest();
  const summary = configLines().map(([k,v])=>`${k}: ${v}`).join('\n');
  const priceBlock = isPremium()
    ? `Price: On request\nA MAGICFX specialist will confirm the Pantone surcharge and final price within 1 business day.`
    : r ? `Total price (excl. VAT): ${eur(r.total)}\nPrice per kg: ${eur(r.perKg)}\nDelivery: ${r.label}` : '';

  return {
    to:      cust.email || '(customer email)',
    subject: `MAGICFX Quote Request — ${ref}`,
    body: `Dear ${cust.name||'(name)'},\n\nThank you for your quote request. We have received your configuration and will get back to you within 1 business day.\n\n───────────────────\nYOUR ORDER SUMMARY\n───────────────────\n${summary}\n\n${priceBlock}\n\nReference number: ${ref}\n\nIf you have any questions, please reply to this email.\n\nKind regards,\nMAGICFX Customer Support\nsupport@magicfx.com`
  };
}

function getPreviewSupport(ref, cust){
  const r   = findBest();
  const sup = supInfo();
  const summary = configLines().map(([k,v])=>`${k}: ${v}`).join('\n');
  const body = [
    `NEW QUOTE REQUEST — ${ref}`,
    `Received: ${new Date().toLocaleString('en-GB')}`,
    ``,
    `─── CUSTOMER ───────────────────────`,
    `Name:     ${cust.name||'(name)'}`,
    `Company:  ${cust.company||'(company)'}`,
    `Email:    ${cust.email||'(email)'}`,
    `VAT:      ${cust.vat||'—'}`,
    `Address:  ${cust.address||'(address)'}`,
    cust.notes ? `Notes:    ${cust.notes}` : null,
    ``,
    `─── CONFIGURATION ──────────────────`,
    summary,
    ``,
    `─── SUPPLIER / INKOOP ──────────────`,
    `Supplier:  ${sup.name||'—'}`,
    `Lead time: ${sup.delivery||'—'}`,
    `Transport: ${sup.transport||'—'}`,
    `Tooling:   ${sup.tooling||'—'}`,
    ``,
    isPremium() ? `⚠ PREMIUM COLOUR — Pantone surcharge TBD. Request supplier quote before confirming price to customer.` : null,
    S.file      ? `⚠ ARTWORK: ${S.file.name} — attached below.` : null,
    ``,
    `Full quote PDF is attached (MAGICFX_Quote_${ref}.html).`,
    ``,
    `— MAGICFX Calculator`,
  ].filter(l => l !== null).join('\n');

  return {
    to:      'support@magicfx.com',
    subject: `[ACTION] Quote ${ref} — ${cust.company||'(company)'}`,
    body
  };
}

// ── Form submit ──────────────────────────────────────
document.getElementById('submitBtn').addEventListener('click', () => {
  const name    = document.getElementById('fName').value.trim();
  const email   = document.getElementById('fEmail').value.trim();
  const company = document.getElementById('fCompany').value.trim();
  const vat     = document.getElementById('fVat').value.trim();
  const address = document.getElementById('fAddress').value.trim();
  const notes   = document.getElementById('fNotes').value.trim();

  const errs = [];
  if(!name)    errs.push('Full name is required.');
  if(!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.push('A valid email address is required.');
  if(!company) errs.push('Company name is required.');
  if(!address) errs.push('Delivery address is required.');

  const errEl = document.getElementById('formErrors');
  if(errs.length){
    errEl.innerHTML = errs.map(e=>`<div>• ${e}</div>`).join('');
    errEl.classList.remove('mfx-hidden');
    errEl.scrollIntoView({behavior:'smooth',block:'nearest'});
    return;
  }
  errEl.classList.add('mfx-hidden');

  const cust = {name, email, company, vat, address, notes};
  const ref  = makeRef();

  // Helper: open mailto without triggering popup blocker
  function openMail(href){
    const a = document.createElement('a');
    a.href = href;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 500);
  }

  // 1. Open quote PDF in new tab
  generatePDF(ref, cust);

  // 2. Customer confirmation mail
  openMail(buildCustomerMail(ref, cust));

  // 3. Support/inkoop mail (small delay so mail client can register first)
  setTimeout(() => openMail(buildSupportMail(ref, cust)), 1200);

  // 4. Show success
  document.getElementById('step06wrap').style.display = 'none';
  document.getElementById('resultPanel').style.display = 'none';
  const sw = document.getElementById('successWrap');
  sw.style.display = '';
  document.getElementById('successTitle').textContent = 'Quote request sent.';
  document.getElementById('successBody').innerHTML =
    `Reference <strong>${ref}</strong> — the quote PDF has been downloaded and two emails have been prepared in your mail client: `+
    `one for <strong>${cust.name}</strong> and one for the support / inkoop team. Review and send both.`;
  document.getElementById('dlPdf').onclick = () => generatePDF(ref, cust);
  sw.scrollIntoView({behavior:'smooth', block:'start'});
});

document.getElementById('newQuote').addEventListener('click', () => location.reload());

// ── Init: inject TEXTS and SETTINGS into DOM ─────────────
function initTexts(){
  const set = (id, val) => { const el=document.getElementById(id); if(el) el.innerHTML=val; };
  set('pageTitle',   TEXTS.page_title);
  set('pageLede',    TEXTS.page_lede);
  set('subShape',    TEXTS.sub_shape);
  set('subColour',   TEXTS.sub_colour);
  set('subSides',    TEXTS.sub_sides);
  set('subQty',      TEXTS.sub_qty);
  set('subDelivery', TEXTS.sub_delivery);
  set('subDetails',  TEXTS.sub_details);
  set('footerNote',  TEXTS.footer_note);
  // eyebrow
  const ey = document.querySelector('.mfx-eyebrow');      if(ey)  ey.textContent  = TEXTS.page_eyebrow;
  const rl = document.querySelector('.mfx-res-label');    if(rl)  rl.textContent  = TEXTS.result_label;
  const fn = document.querySelector('.mfx-submit-note');  if(fn)  fn.textContent  = TEXTS.form_note;
  const up = document.querySelector('.mfx-upload-p');     if(up)  up.textContent  = TEXTS.upload_zone_primary;
  const us = document.querySelector('.mfx-upload-s');     if(us)  us.textContent  = TEXTS.upload_zone_secondary;
  // template strip
  const ti = document.querySelector('.mfx-template-strip-text strong'); if(ti) ti.textContent = TEXTS.template_intro;
  const dl = document.getElementById('templateLink');                if(dl) dl.textContent = TEXTS.template_btn;
  // die guidelines rules
  const dok  = document.querySelector('.mfx-dg.mfx-ok .mfx-dg-rule');  if(dok)  dok.innerHTML  = TEXTS.die_ok_rule;
  const dnok = document.querySelector('.mfx-dg.mfx-nok .mfx-dg-rule'); if(dnok) dnok.innerHTML = TEXTS.die_nok_rule;
  // min kg
  const ki = document.getElementById('kgIn'); if(ki) ki.min = SETTINGS.min_kg;
}

renderAll();
initTexts();
